import { useQuery } from '@tanstack/react-query';
import { useFeatureModule } from './useFeatureModule';
import { useAuthStore } from '@/stores/authStore';
import { messagesApi, parentMessagesApi } from '@/services/api/messages';
import { systemAnnouncementsApi } from '@/services/api/system-announcements';

/**
 * Hook to get split unread counts for nav badges.
 * Returns separate message and announcement counts so they can badge
 * their respective nav links independently.
 *
 * Query key ['messages', 'inbox', 0, 1] is a separate cache entry from
 * the inbox pages. invalidateQueries({ queryKey: ['messages', 'inbox'] })
 * uses partial matching to invalidate both this and the inbox page queries.
 */
export function useUnreadMessageCount(): { messageUnread: number; announcementUnread: number; total: number } {
  const { isEnabled } = useFeatureModule('messaging');
  const { user } = useAuthStore();
  const isStaffRole = user?.role === 'admin' || user?.role === 'teacher' || user?.role === 'support_staff';
  const isParent = user?.role === 'parent';

  const { data } = useQuery({
    queryKey: ['messages', 'inbox', 0, 1],
    queryFn: () => messagesApi.getAll({ inbox: true, limit: 1, offset: 0 }),
    staleTime: 60000,
    refetchInterval: 60000,
    enabled: isEnabled && isStaffRole,
  });

  const { data: parentData } = useQuery({
    queryKey: ['parent-messages', 'unread-count'],
    queryFn: () => parentMessagesApi.getUnreadCount(),
    staleTime: 60000,
    refetchInterval: 60000,
    enabled: isEnabled && isParent,
  });

  const { data: announcementsData } = useQuery({
    queryKey: ['system-announcements', 'unread-count'],
    queryFn: () => systemAnnouncementsApi.getAll(1, 0),
    staleTime: 60000,
    refetchInterval: 60000,
    enabled: isStaffRole || isParent,
  });

  const messageUnread = isParent ? (parentData?.data?.unreadCount ?? 0) : (data?.data?.unreadCount ?? 0);
  const announcementUnread = announcementsData?.data?.unreadCount ?? 0;
  return { messageUnread, announcementUnread, total: messageUnread + announcementUnread };
}
